import React, { useState } from 'react';
import { jsPDF } from 'jspdf';
import { WardDetails } from '../../Datas/WardDetails';  
import './DrugConsumptionTable.css';
import ModalDC from './ModalDC.jsx';
import DrugConsumptionChart from '../DrugConsumptionGraph/DrugConsumptioncChart.jsx'; // Import the chart component

const DrugConsumptionTable = () => {
  const [filteredData, setFilteredData] = useState(WardDetails[0].DrugConsumption);  
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isMedicinesWithPatient, setIsMedicinesWithPatient] = useState(false);
  const [timeRange, setTimeRange] = useState("custom"); 
  const [isModalOpen, setIsModalOpen] = useState(false); 
  const [selectedPatient, setSelectedPatient] = useState(null);

  const handleTimeRangeChange = (e) => {
    setTimeRange(e.target.value);
    const now = new Date();
    let newStartDate = new Date(now);
    let newEndDate = new Date(now);

    switch (e.target.value) {
      case "1-week":
        newStartDate.setDate(now.getDate() - 7); 
        setStartDate(newStartDate.toISOString().split('T')[0]);
        setEndDate(newEndDate.toISOString().split('T')[0]);
        break;
      case "1-month":
        newStartDate.setMonth(now.getMonth() - 1); 
        setStartDate(newStartDate.toISOString().split('T')[0]);
        setEndDate(newEndDate.toISOString().split('T')[0]);
        break;
      case "1-year":
        newStartDate.setFullYear(now.getFullYear() - 1); // Set to 1 year ago
        setStartDate(newStartDate.toISOString().split('T')[0]);
        setEndDate(newEndDate.toISOString().split('T')[0]);
        break;
      default:
        setStartDate("");
        setEndDate("");
        break;
    }
  };

  const filterByDateRange = () => {
    if (startDate && endDate) {
      const filtered = WardDetails[0].DrugConsumption.filter(patient => {
        const admissionDate = new Date(patient.admissionDate);
        const dischargeDate = new Date(patient.dischargeDate);
        return admissionDate >= new Date(startDate) && dischargeDate <= new Date(endDate);
      });
      setFilteredData(filtered);
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    let y = 20;
    let totalRevenue = 0;

    doc.setFontSize(14);
    if (isMedicinesWithPatient) {
      doc.text("Drug Consumption Report (All Patients with Medicines)", 20, y);
      y += 10;
      filteredData.forEach((patient) => {
        doc.setFontSize(12);
        doc.text(`Patient: ${patient.patientName}`, 20, y);
        doc.text(`Gender: ${patient.gender}`, 20, y + 10);  
        y += 20;
        patient.medications.forEach((med, index) => {
          doc.text(med.medicationName, 20, y);
          doc.text(med.prescribedQuantity.toString(), 120, y);
          doc.text(med.consumedQuantity.toString(), 160, y);
          doc.text(`₹${med.totalCost}`, 200, y);
          doc.text(med.company, 240, y);
          totalRevenue += med.totalCost;
          y += 10;
        });
        y += 20;
      });
    } else {
      doc.text("Drug Consumption Report (Medicines Only)", 20, y);
      y += 10;
      filteredData.forEach((patient) => {
        patient.medications.forEach((med) => {
          doc.setFontSize(12);
          doc.text(med.medicationName, 20, y);
          doc.text(med.prescribedQuantity.toString(), 120, y);
          doc.text(med.consumedQuantity.toString(), 160, y);
          doc.text(`₹${med.totalCost}`, 200, y);
          doc.text(med.company, 240, y);
          totalRevenue += med.totalCost;
          y += 10;
        });
      });
    }

    doc.setFontSize(12);
    doc.text(`Total Revenue: ₹${totalRevenue}`, 20, y);
    doc.save("drug_consumption_report.pdf");
  };

  const openModal = (patient) => {
    setSelectedPatient(patient);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPatient(null);
  };

  return (
    <div className="drug-consumption-container">
      <div className="filter-container">
        <select value={timeRange} onChange={handleTimeRangeChange} className="time-range-dropdown">
          <option value="custom">Custom Date Range</option>
          <option value="1-week">Last 1 Week</option>
          <option value="1-month">Last 1 Month</option>
          <option value="1-year">Last 1 Year</option>
        </select>
        {timeRange === "custom" && (
          <>
            <input
              className="date-input"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <input
              className="date-input"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </>
        )}
        <button className="filter-button-DC" onClick={filterByDateRange}>Filter</button>
        <div className="export-options">
          <label className="export-option-label">
            <input
              className="export-option-radio"
              type="radio"
              name="exportType"
              checked={!isMedicinesWithPatient}
              onChange={() => setIsMedicinesWithPatient(false)}
            />
            Export Medicines Only
          </label>
          <label className="export-option-label">
            <input
              className="export-option-radio"
              type="radio"
              name="exportType"
              checked={isMedicinesWithPatient}
              onChange={() => setIsMedicinesWithPatient(true)}
            />
            Export Medicines with Patient Details
          </label>
        </div>
      </div>

      <div className="patients-data-container">
        {isMedicinesWithPatient ? (
          filteredData.map((patient, index) => (
            <div key={index} className="patient-container">
              <h3 className="patient-name">
                {patient.patientName}
              </h3>
              <table className="medication-table">
                <thead>
                  <tr className="table-header">
                    <th>Medication</th>
                    <th>Prescribed Quantity</th>
                    <th>Consumed Quantity</th>
                    <th>Company</th>
                    <th>Total Cost (Rs)</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {patient.medications.map((med, index) => (
                    <tr key={index}>
                      <td>{med.medicationName}</td>
                      <td>{med.prescribedQuantity}</td>
                      <td>{med.consumedQuantity}</td>
                      <td>{med.company}</td>
                      <td>₹{med.totalCost}</td>
                      {index === 0 && (
                        <td rowSpan={patient.medications.length}>
                          <button onClick={() => openModal(patient)}>Details</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
        ) : (
          <table className="medication-table">
            <thead>
              <tr className="table-header">
                <th>Medication</th>
                <th>Prescribed Quantity</th>
                <th>Sold Quantity</th>
                <th>Company</th>
                <th>Total Cost (Rs)</th>
                <th>Gender</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((patient, index) =>
                patient.medications.map((med, index2) => (
                  <tr key={`${index}-${index2}`}>
                    <td>{med.medicationName}</td>
                    <td>{med.prescribedQuantity}</td>
                    <td>{med.consumedQuantity}</td>
                    <td>{med.company}</td>
                    <td>₹{med.totalCost}</td>
                    <td>{patient.gender}</td> 
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="export-button-container">
        <button className="export-button" onClick={exportToPDF}>Export as PDF</button>
      </div>

      <DrugConsumptionChart data={filteredData} /> {/* Move the chart below the table */}

      {isModalOpen && <ModalDC patient={selectedPatient} onClose={closeModal} />}
      
    </div>
  );
};

export default DrugConsumptionTable;
